// src/clock/state.js — clock state cache + accessors (v87.0)
//
// Extracted from index.html in v87.0. Previously lived as top-level globals
// at lines 2345–2366 (state decls + getClockEventForDate), 2468–2483
// (getClockForWeek), and 2550–2641 (loadClockFromDB) in index.html v86.16.
//
// What's here:
//   - clockEvents       → today's live clock state, keyed by staff_id
//   - clockHistoryByDate → per-date cache for past-date views (Time Cards)
//   - getClockEventForDate → router: live vs. history based on the date
//   - getClockForWeek   → week-summary lookup (current week only)
//   - loadClockFromDB   → DB fetch + group-by-staff + populate above caches
//
// Why module-private state instead of window-globals: the v86.11 → v86.15
// fiasco showed that "this state is shared globally" is exactly the kind of
// implicit contract that breaks silently. With the references gathered here
// and exposed only via the Clock.* surface, every external mutator becomes
// visible in CI (the test file scans index.html for bare `clockEvents`).
//
// Free-variable dependencies (resolved against window at call time):
//   businessDayStr (dates.js), localDateStr (dates.js), getMonday (dates.js),
//   timeMins (dates.js), DB (db.js), dlog (index.html top-level).

import { isCarryableOpenShift } from '../shared/clock-rules.js';
import { utcOffsetHoursFor } from '../shared/clock-rules.js';   // v136.9: the store's offset, so the 12 hours are real hours

// clockEvents: { staffId: {status, clockIn, clockOut, breakStart, log:[]} }
// IMPORTANT: clockEvents holds TODAY's state only. Live actions (clock_in,
// clock_out, breaks) mutate this map directly. For past-date views, see
// clockHistoryByDate below and the getClockEventForDate() router.
export let clockEvents = {};
// v129.99 (9 Aug e2e-flake root cause) — the READ-side ordering guard.
// The login flow fires loadClockFromDB() fire-and-forget; on a slow network
// its response could land AFTER the first optimistic clock action, and the
// destructive today-reset below wiped the fresh `in` state — the screen
// rolled back to "Clock In" while the DB row was fine (the evening e2e
// flake; real stores on slow Wi-Fi hit the same rollback). Every mutation
// bumps this generation; a load that started before the bump discards its
// stale snapshot instead of applying it. Last ACTION wins, not last response.
let clockGen = 0;
export function bumpClockGen() { clockGen++; }
// v136.8: counts the times a load of TODAY reset clockEvents. That reset also
// drops a shift carried over from the previous business day, which only the
// stuck check puts back; so the throttled check (stuck.js) never skips after a
// reset it has not yet answered.
let clockResetGen = 0;
export function getClockResetGen() { return clockResetGen; }
// v79.15.1: per-date clock history. Populated on demand when the user
// navigates to a past date in the Time Cards screen. Keyed by YYYY-MM-DD,
// each entry is an object keyed by staff_id with the same shape as
// clockEvents entries (status, clockIn, clockOut, breakStart, breakMins,
// log, shiftDate). NEVER mutated by live clock actions — past dates are
// read-only from this view. Survives midnight rollover (clockEvents does not).
export let clockHistoryByDate = {};
// Look up a clock event for a given staff member on a given date.
// Routes to live clockEvents for today, clockHistoryByDate for past dates.
// Returns null if nothing is loaded yet for that date+staff combination.
export function getClockEventForDate(staffId, dateStr) {
  // v80.15: business-day boundary so a 02:30 lookup on Saturday treats Friday
  // as "today" — clockEvents is populated for the business day, not calendar day
  var today = businessDayStr();
  if (dateStr === today) return clockEvents[staffId] || null;
  return (clockHistoryByDate[dateStr] && clockHistoryByDate[dateStr][staffId]) || null;
}

// Returns clock event for a specific staff member on a specific day+week.
// Only live clockEvents (current week) are available; past-week history
// will come from clock_log DB reads in a future iteration.
export function getClockForWeek(staffId, weekStart, day) {
  var wk = weekStart instanceof Date ? localDateStr(weekStart) : weekStart;
  var currentWk = localDateStr(getMonday(new Date()));
  if (wk !== currentWk) return null;
  if (day) return clockEvents[staffId] || null;
  // Week summary — rough estimate from live clock entry
  var ev = clockEvents[staffId];
  if (ev && ev.clockIn && ev.clockOut) {
    var h = (timeMins(ev.clockOut) - timeMins(ev.clockIn)) / 60 - 1;
    return {totalH: Math.max(0, Math.round(h * 10) / 10)};
  }
  return null;
}

// Populate clockEvents[] from DB for a given date (defaults to today).
// Merges multiple rows for the same staff+date into one entry
// (earliest clock_in wins, latest clock_out wins).
export async function loadClockFromDB(dateStr) {
  if (typeof DB === 'undefined') return;
  // v80.15: when called with no arg, default to BUSINESS day (03:00–03:00).
  // At 00:30 May 9, business day = May 8, so a fresh load picks up open
  // clock-in rows from May 8 — without this, clockEvents stays empty and
  // staff see "Not yet clocked in" for shifts they haven't actually closed.
  var date = dateStr || businessDayStr();
  try {
    var gen = clockGen;   // v129.99: snapshot the mutation generation BEFORE the fetch
    // v131.38: tokenless reads name the store (an AM/HO login carries a token and may omit it).
    var rows = await DB.clock.list({ date: date, store: (typeof currentStore !== 'undefined' && currentStore) ? currentStore : undefined });
    if (!Array.isArray(rows)) return;
    // v129.99: a clock action happened while this read was in flight — its
    // snapshot is stale. Applying it would wipe the optimistic state and roll
    // the screen back. The action's own post-save reconcile re-reads truth.
    if (gen !== clockGen) return;
    // Only reset events for the date we're loading (don't wipe in-progress current-day state
    // if we're loading a different date for history view)
    var isToday = (date === businessDayStr());
    if (isToday) {
      // For today: reset to what the DB says (authoritative)
      Object.keys(clockEvents).forEach(function(k){ delete clockEvents[k]; });
      clockResetGen++;   // v136.8: the carried shift is gone until the stuck check runs again
    }
    // Group by staff_id.
    //
    // v129.158 (split shifts step 6) — a day can now hold SEVERAL sessions
    // (seq 1, 2, …). The live card must reflect the CURRENT session: the open
    // row if one exists, else the highest-seq (latest) closed row. The old
    // earliest-in/latest-out merge was built for duplicate-row noise in the
    // pre-unique era; on a split day it fused the morning's clock-in with the
    // night's clock-out into one 22-hour phantom. log[] still lists every
    // session's events so the card history shows the whole day.
    var byStaff = {};
    rows.forEach(function(r){
      (byStaff[r.staff_id] = byStaff[r.staff_id] || []).push(r);
    });
    var grouped = {};
    Object.keys(byStaff).forEach(function(sid){
      var list = byStaff[sid].slice().sort(function(a,b){ return (a.seq||1)-(b.seq||1) || (a.id||0)-(b.id||0); });
      // The live session: an OPEN row wins; otherwise the last (highest seq).
      var live = list.filter(function(r){ return r.clock_in && !r.clock_out; })[0] || list[list.length-1];
      var hhmm = function(ts){ return ts ? (String(ts).split('T')[1]?.slice(0,5) || String(ts).slice(11,16)) : null; };
      var g = grouped[sid] = {
        clockIn:  hhmm(live.clock_in),
        clockOut: hhmm(live.clock_out),
        breakMins: live.break_mins || 0,
        breakStart: hhmm(live.break_start),
        shiftDate: live.clock_in ? live.date : null,   // v79.8 anchor, per-session
        seq: live.seq || 1,                            // v129.158 — which session is live
        log: [],
      };
      list.forEach(function(r){
        var inT = hhmm(r.clock_in), outT = hhmm(r.clock_out);
        if (inT)  g.log.push({action:'clock_in',  time: inT});
        if (outT) g.log.push({action:'clock_out', time: outT});
      });
    });
    if (isToday) {
      Object.keys(grouped).forEach(function(id){
        var g = grouped[id];
        // Status priority: if breakStart set → on break; else clocked_out? → out; else clocked_in? → in
        var status = 'out';
        if (g.breakStart) status = 'break';
        else if (g.clockOut) status = 'out';
        else if (g.clockIn) status = 'in';
        clockEvents[parseInt(id)] = {
          status: status,
          clockIn: g.clockIn || undefined,
          clockOut: g.clockOut || undefined,
          breakStart: g.breakStart || undefined,
          breakMins: g.breakMins || 0,
          shiftDate: g.shiftDate || undefined,
          seq: g.seq || 1,          // v129.158 — the live session number   // v79.8: preserved from DB
          log: g.log,
        };
      });
    } else {
      // v79.15.1: past-date load — write to per-date cache instead of dropping
      // grouped on the floor. Same shape as the today branch so getClockEventForDate
      // returns identical objects regardless of which path populated them.
      var bucket = {};
      Object.keys(grouped).forEach(function(id){
        var g = grouped[id];
        var status = 'out';
        if (g.breakStart) status = 'break';
        else if (g.clockOut) status = 'out';
        else if (g.clockIn) status = 'in';
        bucket[parseInt(id)] = {
          status: status,
          clockIn: g.clockIn || undefined,
          clockOut: g.clockOut || undefined,
          breakStart: g.breakStart || undefined,
          breakMins: g.breakMins || 0,
          shiftDate: g.shiftDate || undefined,
          seq: g.seq || 1,          // v129.158 — the live session number
          log: g.log,
        };
      });
      clockHistoryByDate[date] = bucket;
    }
    dlog('[DB] Loaded ' + Object.keys(grouped).length + ' clock records for ' + date);
    return grouped;
  } catch(e) {
    console.warn('[DB] Clock load failed:', e.message);
  }
}

// HH:MM out of a "YYYY-MM-DDTHH:MM:SS" (or similar) timestamp string.
function _hhmm(ts) {
  var p = String(ts == null ? '' : ts).split('T')[1];
  return p ? p.slice(0, 5) : String(ts).slice(11, 16);
}

// v123.x — carry an OPEN shift across the 3am business-day roll-over.
//
// Bug it fixes: a staffer clocks in at, say, 23:00 and is still on the floor
// after 03:00. The business day has rolled to the NEW day, so a fresh
// loadClockFromDB() populates clockEvents for the new day — empty for them —
// and the clock screen offers "Clock In" instead of "Clock Out". They can't
// close their shift (pay-critical for late finishers).
//
// Fix: merge their still-open session from the PREVIOUS business day back into
// today's clockEvents, flagged carriedOver, with shiftDate = the start day.
// The card then shows "On floor (yesterday)" and the NORMAL clock-out path runs
// — doClock()/tsFor() already stamp an overnight clock_out onto the next day
// against the start-day row (the tested v79.8 overnight logic), so pay is
// recorded correctly with no new write path.
//
// v129.148 — SCOPED BY THE SHIFT'S AGE, NOT BY THE WALL CLOCK.
//
// This was `if (nowHour >= 6) return` — before 06:00, on the theory that only
// then is an open previous-day session plausibly "still on the floor". That
// proxy is wrong for the shift that matters most: overnight restocking runs
// 22:00 to 07:00, so the carry switched itself off an hour BEFORE the crew
// finished, and the person standing at the till was offered "Clock In".
//
// Measured on prod: 26 of 26 overnight shifts across four stocktake nights
// never got a clock-out. The control is the same night at the same store — a
// 00:16 clock-out worked fine, because it fell inside the old window.
//
// The right question is how long the shift has been running, not what time it
// is now: a 22:00 start is real at 07:00 (9h) and abandoned at 14:00 (16h).
// MAX_OPEN_SHIFT_HOURS is the line, shared with the Worker's canary.
//
// Residual, accepted deliberately: someone who forgets and clocks out at 09:00
// for a 07:00 finish records ~10h instead of 9h, which an AM amends. The
// alternative is the status quo — no clock-out at all, 26 times out of 26 —
// and a slightly long shift that a human can correct beats a missing one that
// nobody notices for three weeks.
//
// Pure over (rows, clockEvents): `rows` = open-session rows (clock_in set,
// clock_out null). Returns a map of merged staff_ids so the caller can keep
// them OUT of the forgot-to-clock-out banner (no double-surfacing).
export function mergeCarriedOpenShifts(rows, nowMs) {
  var merged = {};
  if (!Array.isArray(rows)) return merged;
  var now = (typeof nowMs === 'number' && !isNaN(nowMs)) ? nowMs : Date.now();
  var todayBiz = businessDayStr();
  var pd = new Date(todayBiz + 'T12:00:00');
  pd.setDate(pd.getDate() - 1);
  var prev = localDateStr(pd);                     // the previous business day
  rows.forEach(function(r){
    if (!r || r.date !== prev || !r.clock_in || r.clock_out) return;  // must be open, on the previous day
    // v129.148: within the 12h ceiling, else it is the banner's. v136.9: 12 REAL hours, so the row's store's offset
    // (Qatar UTC+3, the UAE UTC+4: clock_in is that store's wall time); isQatarStore is the helpers bundle's global.
    var qatar = false;
    try { qatar = typeof isQatarStore === 'function' && !!isQatarStore(r.store || ''); } catch (e) { qatar = false; }
    if (!isCarryableOpenShift(r, now, utcOffsetHoursFor(qatar))) return;
    var sid = parseInt(r.staff_id);
    var cur = clockEvents[sid];
    // A FRESH shift started today wins (don't overwrite it, and leave the old
    // open session to the forgot-to-clock-out banner). Re-merging an
    // already-carried entry is fine — that keeps it classified on every re-run.
    if (cur && (cur.status === 'in' || cur.status === 'break') && !cur.carriedOver) return;
    var inT = _hhmm(r.clock_in);
    clockEvents[sid] = {
      // v136.8 (16 Oct audit): on break when the open row has a break_start (break end CLEARS break_start;
      // there is no break_end column), exactly as loadClockFromDB decides it. It always said 'in', so a
      // carried shift on break offered Clock Out instead of End Break.
      status: r.break_start ? 'break' : 'in',
      clockIn: inT || undefined,
      clockOut: undefined,
      breakStart: r.break_start ? _hhmm(r.break_start) : undefined,
      breakMins: r.break_mins || 0,
      shiftDate: r.date,        // anchors the overnight clock_out to the start day
      // v136.8 (16 Oct audit): WHICH session of that day this is. Without it doClock sent seq 1, so clocking
      // out an overnight SECOND session (day shift 10-18 closed, then 22:00-07:00) closed session 1 instead,
      // overwriting the finished day shift, and the break patch targeted the same wrong row.
      seq: r.seq || 1,
      carriedOver: true,
      log: inT ? [{ action: 'clock_in', time: inT }] : [],
    };
    merged[sid] = true;
  });
  return merged;
}
